/**
 * Cart state shared app-wide (sidebar/header badges, marketplace, checkout).
 * SECURITY: UX-only line items; backend reprices. Never payment data.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Bundle, CartItem } from '../server/api';
import { useAuth } from './AuthContext';

const CART_STORAGE_KEY = 'shieldsme_cart_v1';
const MAX_CART_ITEMS = 20;

const BUNDLE_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function newCartItemId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function validCartItem(raw: unknown): raw is CartItem {
  if (!raw || typeof raw !== 'object') return false;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.name !== 'string') return false;
  if (o.type !== 'bundle' && o.type !== 'individual') return false;
  const q = o.quantity;
  if (typeof q !== 'number' || !Number.isInteger(q) || q < 1 || q > 50) return false;
  if (typeof o.unit_price !== 'number' || !Number.isFinite(o.unit_price)) return false;
  if (typeof o.currency !== 'string' || o.currency.length < 3) return false;
  if (o.type === 'bundle') {
    if (typeof o.bundle_id !== 'string' || !BUNDLE_ID_RE.test(o.bundle_id)) return false;
  } else if (typeof o.asset_type !== 'string' || !o.asset_type.trim()) {
    return false;
  }
  return true;
}

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(validCartItem).slice(0, MAX_CART_ITEMS);
  } catch {
    return [];
  }
}

function persistCartToStorage(items: CartItem[]): void {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* localStorage unavailable */
  }
}

export type CartContextValue = {
  items: CartItem[];
  addBundle: (bundle: Bundle) => void;
  addIndividual: (
    asset_type: string,
    name: string,
    quantity: number,
    unit_price: number,
    currency: string
  ) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  subtotal: number;
  currency: string;
};

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  const prevUserIdRef = useRef<string | undefined>(undefined);

  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    persistCartToStorage(items);
  }, [items]);

  useEffect(() => {
    const uid = currentUser?.id;
    const prev = prevUserIdRef.current;
    prevUserIdRef.current = uid;
    if (prev !== undefined && prev !== uid) {
      setItems([]);
      try {
        localStorage.removeItem(CART_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [currentUser?.id]);

  useEffect(() => {
    const sync = () => setItems(loadCart());
    const onStorage = (e: StorageEvent) => {
      if (e.key === CART_STORAGE_KEY) sync();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addBundle = useCallback((bundle: Bundle) => {
    setItems((prev) => {
      if (prev.some((i) => i.type === 'bundle' && i.bundle_id === bundle.id)) return prev;
      if (prev.length >= MAX_CART_ITEMS) return prev;
      const newItem: CartItem = {
        id: newCartItemId(),
        type: 'bundle',
        bundle_id: bundle.id,
        bundle_key: bundle.bundle_key,
        name: bundle.name,
        quantity: 1,
        unit_price: bundle.discounted_price,
        currency: bundle.currency,
        discount_percent: bundle.discount_percent,
      };
      return [...prev, newItem];
    });
  }, []);

  const addIndividual = useCallback(
    (asset_type: string, name: string, quantity: number, unit_price: number, currency: string) => {
      setItems((prev) => {
        const existing = prev.find((i) => i.type === 'individual' && i.asset_type === asset_type);
        if (existing) {
          return prev.map((i) =>
            i.id === existing.id
              ? { ...i, quantity: Math.min(50, Math.max(1, Math.floor(i.quantity + quantity))) }
              : i
          );
        }
        if (prev.length >= MAX_CART_ITEMS) return prev;
        const q = Math.min(50, Math.max(1, Math.floor(quantity)));
        return [
          ...prev,
          {
            id: newCartItemId(),
            type: 'individual',
            asset_type,
            name,
            quantity: q,
            unit_price,
            currency,
          },
        ];
      });
    },
    []
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    const safe = Math.min(50, Math.max(1, Math.floor(quantity)));
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: safe } : i)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const totalItems = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const subtotal = useMemo(
    () => Math.round(items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0) * 100) / 100,
    [items]
  );
  const currency = items[0]?.currency ?? 'USD';

  const value = useMemo(
    () => ({
      items,
      addBundle,
      addIndividual,
      removeItem,
      updateQuantity,
      clearCart,
      totalItems,
      subtotal,
      currency,
    }),
    [items, addBundle, addIndividual, removeItem, updateQuantity, clearCart, totalItems, subtotal, currency]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
